import Phaser from 'phaser'

import type { Character } from '~/game/Character'
import type { LoungeLayout } from '~/game/world'

const CHATTER = [
  'coffee?',
  'good weekend?',
  "how's the sprint?",
  'nice work earlier',
  'brb, refill',
  'did you see that?',
  'almost Friday',
  'standup was short!',
  '☕',
  'hah, true',
]
const EMOTES = ['☕', '♪', '✦', '♥', '✓']
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)]

export interface WaterCoolerDeps {
  /** All on-floor characters by userId (the scene's `chars` map). */
  chars: ReadonlyMap<string, Character>
  /** Lounge geometry from computeLayout. */
  lounge: LoungeLayout
  /** True if userId is online (presence === 'online') — humans + always-on agents. */
  isOnline: (userId: string) => boolean
  /** True if userId is in a meeting OR otherwise unavailable to wander. */
  isUnavailable: (userId: string) => boolean
  /** Cooperative busy-lock shared with the scene's real animations. */
  isBusy: (userId: string) => boolean
  markBusy: (userId: string, on: boolean) => void
}

/**
 * Ambient water-cooler social. On a slow, jittered timer it pulls 2–3 ONLINE,
 * idle people over to a lounge spot OUTSIDE the room grid, lets them linger with
 * a tiny chat bubble/emote, then walks each back to their home seat.
 *
 * STRICT decorative contract:
 *  - never touches the EventBus (no emit, no on) — real message/task/meeting
 *    animations are unaffected, and it can't trip boards/sfx/topic pills;
 *  - never mutates the snapshot, layout, placements, vacant, or seatPos;
 *  - never moves anyone unavailable (in a meeting) or already busy (mid real
 *    animation), and releases its busy-lock the instant a person heads home;
 *  - fully pausable via the returned stop() (clears the timer + sends any
 *    lingering wanderers home and frees their locks). Uses scene.time so it dies
 *    with the scene — no leaked window timers.
 */
export function startWaterCooler(
  scene: Phaser.Scene,
  deps: WaterCoolerDeps,
): () => void {
  const { chars, lounge, isOnline, isUnavailable, isBusy, markBusy } = deps
  let stopped = false
  // Who WE currently have out at the cooler (so we can recall them on stop).
  const wandering = new Set<string>()
  let timer: Phaser.Time.TimerEvent | undefined

  const sendHome = (userId: string): void => {
    const char = chars.get(userId)
    wandering.delete(userId)
    if (char) char.goHome()
    markBusy(userId, false)
  }

  const gather = (): void => {
    if (stopped) return
    // Eligible = online HUMANS, available, not already busy, not wandering.
    // Agents are excluded — they're always-online, so they'd otherwise hog
    // every huddle in agent-heavy orgs (and bots don't do small talk).
    const eligible = [...chars.values()].filter(
      (c) =>
        !c.isAgent &&
        isOnline(c.userId) &&
        !isUnavailable(c.userId) &&
        !isBusy(c.userId) &&
        !wandering.has(c.userId),
    )
    if (eligible.length < 2) return // need a little crowd; otherwise skip a beat

    Phaser.Utils.Array.Shuffle(eligible)
    const group = eligible.slice(0, Phaser.Math.Between(2, 3))
    const spots = lounge.spots

    group.forEach((char, i) => {
      const spot = spots[i % spots.length]
      // Tiny per-person jitter so two people don't perfectly overlap.
      const tx = spot.x + Phaser.Math.Between(-7, 7)
      const ty = spot.y + Phaser.Math.Between(-5, 5)
      wandering.add(char.userId)
      markBusy(char.userId, true)
      // Failsafe: if a REAL animation interrupts the walk, the walkTo onArrive
      // below never fires (stopped tweens don't complete) and the linger timers
      // are never scheduled — without this sweep they'd stay "wandering" (and
      // excluded from future huddles) forever.
      scene.time.delayedCall(20000, () => {
        if (!stopped && wandering.has(char.userId)) sendHome(char.userId)
      })
      char.walkTo(tx, ty, () => {
        // If we got paused / they became unavailable while walking, bail home.
        if (stopped || isUnavailable(char.userId)) {
          sendHome(char.userId)
          return
        }
        // Linger: stagger a little chatter, then drift home after a beat.
        scene.time.delayedCall(300 + i * 700, () => {
          if (stopped || !wandering.has(char.userId)) return
          if (Math.random() < 0.7) char.say(pick(CHATTER))
          else char.reactionPop(pick(EMOTES))
        })
        const linger = Phaser.Math.Between(4200, 6500)
        scene.time.delayedCall(linger, () => {
          if (!wandering.has(char.userId)) return // already recalled
          sendHome(char.userId)
        })
      })
    })
  }

  // RARE, jittered cadence — a water-cooler huddle is an occasional treat, not a
  // constant churn. Only ~1 in 6 of these slow beats actually gathers, so on
  // average a huddle forms just once every several minutes. First beat lands
  // well after the opening camera/spawn.
  timer = scene.time.addEvent({
    delay: 60000,
    startAt: -15000, // first possible huddle ~75s in
    loop: true,
    callback: () => {
      if (Math.random() < 0.16) gather() // skip ~84% of beats
    },
  })

  return () => {
    if (stopped) return
    stopped = true
    timer?.remove(false)
    timer = undefined
    for (const userId of [...wandering]) sendHome(userId)
  }
}
